import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, ChevronRight, Clock, MapPin } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { isSameCityDemand } from '@/lib/inAppNotifications';
import { serviceImage, serviceTint, statusMeta } from '@/lib/domain';
import { formatSlot } from '@/lib/format';
import { useUserId } from '@/components/AuthProvider';
import { EmptyState, StatusBadge } from '@/components/ui';
import Spinner from '@/components/Spinner';
import styles from './Requests.module.css';

type Tab = 'missions' | 'demandes';

/** Canonical lifecycle order, so the filter chips always read left-to-right. */
const STATUS_ORDER = [
  'pending',
  'assigned',
  'accepted',
  'in_progress',
  'pending_review',
  'completed',
  'cancelled',
  'rejected',
];

type Mission = {
  id: string;
  client_id: string;
  status: string;
  address: string;
  preferred_date: string | null;
  preferred_time: string | null;
  service_name: string;
};

type Demand = {
  id: string;
  address: string;
  description: string | null;
  preferred_date: string | null;
  preferred_time: string | null;
  service_name: string;
};

export default function ProviderRequests() {
  const navigate = useNavigate();
  const userId = useUserId();
  // The tab lives in the URL (?tab=available) so ApplyBooking and notifications can return to it.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: Tab = searchParams.get('tab') === 'available' ? 'demandes' : 'missions';
  const setTab = (next: Tab) =>
    setSearchParams({ tab: next === 'demandes' ? 'available' : 'missions' }, { replace: true });
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [missions, setMissions] = useState<Mission[]>([]);
  const [demands, setDemands] = useState<Demand[]>([]);
  const [appliedIds, setAppliedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  // Kept so the empty state can explain *why* demands were filtered out.
  const [providerCity, setProviderCity] = useState('');
  const [myServiceNames, setMyServiceNames] = useState<string[]>([]);
  const [pendingTotal, setPendingTotal] = useState(0);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [
        { data: missionsData },
        { data: demandsData },
        { data: proposalsData },
        { data: providerServices },
      ] = await Promise.all([
        supabase
          .from('bookings')
          .select(
            'id, client_id, status, address, preferred_date, preferred_time, services(name), created_at'
          )
          .eq('provider_id', userId)
          .order('created_at', { ascending: false }),
        supabase
          .from('bookings')
          .select(
            'id, service_id, address, description, preferred_date, preferred_time, services(name), created_at'
          )
          .eq('status', 'pending')
          .order('created_at', { ascending: false }),
        supabase.from('booking_proposals').select('booking_id').eq('provider_id', userId),
        supabase
          .from('provider_services')
          .select('service_id, services(name)')
          .eq('provider_id', userId),
      ]);

      const { data: profileData } = await supabase
        .from('profiles')
        .select('city')
        .eq('id', userId)
        .maybeSingle();

      const { data: applicationData } = await supabase
        .from('provider_applications')
        .select('city')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const city = profileData?.city || applicationData?.city || '';
      setProviderCity(city);
      setMyServiceNames(
        (providerServices ?? [])
          .map((row: any) => row.services?.name)
          .filter(Boolean) as string[]
      );
      setPendingTotal(demandsData?.length ?? 0);

      setMissions(
        (missionsData ?? []).map((row: any) => ({
          id: row.id,
          client_id: row.client_id,
          status: row.status,
          address: row.address,
          preferred_date: row.preferred_date,
          preferred_time: row.preferred_time,
          service_name: row.services?.name ?? 'Service',
        }))
      );

      // Only demands matching this provider's services and city are actionable.
      const myServiceIds = new Set((providerServices ?? []).map((row: any) => row.service_id));
      setDemands(
        (demandsData ?? [])
          .filter(
            (booking: any) =>
              myServiceIds.has(booking.service_id) && isSameCityDemand(booking, city)
          )
          .map((row: any) => ({
            id: row.id,
            address: row.address,
            description: row.description,
            preferred_date: row.preferred_date,
            preferred_time: row.preferred_time,
            service_name: row.services?.name ?? 'Service',
          }))
      );

      setAppliedIds(new Set((proposalsData ?? []).map((row: any) => row.booking_id)));
    } catch (err) {
      console.error('Error loading provider bookings:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Only offer chips for statuses the provider actually has.
  const statusCounts = useMemo(() => {
    const counts = new Map<string, number>();
    missions.forEach((mission) => {
      counts.set(mission.status, (counts.get(mission.status) ?? 0) + 1);
    });
    return counts;
  }, [missions]);

  const availableStatuses = useMemo(
    () => STATUS_ORDER.filter((status) => statusCounts.has(status)),
    [statusCounts]
  );

  const visibleMissions = useMemo(
    () =>
      statusFilter === 'all'
        ? missions
        : missions.filter((mission) => mission.status === statusFilter),
    [missions, statusFilter]
  );

  const openMissionChat = async (mission: Mission) => {
    if (!userId || !mission.client_id) return;

    let { data: room } = await supabase
      .from('chat_rooms')
      .select('id')
      .eq('booking_id', mission.id)
      .maybeSingle();

    if (!room) {
      const { data: created } = await supabase
        .from('chat_rooms')
        .insert({
          booking_id: mission.id,
          client_id: mission.client_id,
          provider_id: userId,
        })
        .select('id')
        .single();
      room = created;
    }

    if (room) navigate(`/pro/messages/${room.id}`);
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Demandes</h1>
        <div className={styles.tabs} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'missions'}
            className={`${styles.tab} ${tab === 'missions' ? styles.tabOn : ''}`}
            onClick={() => setTab('missions')}
          >
            Mes missions <strong>({missions.length})</strong>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'demandes'}
            className={`${styles.tab} ${tab === 'demandes' ? styles.tabOn : ''}`}
            onClick={() => setTab('demandes')}
          >
            Demandes disponibles <strong>({demands.length})</strong>
          </button>
        </div>
      </header>

      <div className={styles.body}>
        {loading ? (
          <Spinner block />
        ) : tab === 'missions' ? (
          missions.length === 0 ? (
            <EmptyState
              illustration="/assets/illustrations/clipboard.png"
              title="Aucune mission disponible"
              text="Vos missions apparaîtront ici une fois assignées."
            />
          ) : (
            <>
              <div className={styles.filters} role="group" aria-label="Filtrer par statut">
                <button
                  type="button"
                  className={statusFilter === 'all' ? 'chip chip--on' : 'chip'}
                  onClick={() => setStatusFilter('all')}
                  aria-pressed={statusFilter === 'all'}
                >
                  Toutes <span className={styles.filterCount}>{missions.length}</span>
                </button>

                {availableStatuses.map((status) => (
                  <button
                    key={status}
                    type="button"
                    className={statusFilter === status ? 'chip chip--on' : 'chip'}
                    onClick={() => setStatusFilter(status)}
                    aria-pressed={statusFilter === status}
                  >
                    {statusMeta(status).label}{' '}
                    <span className={styles.filterCount}>{statusCounts.get(status)}</span>
                  </button>
                ))}
              </div>

              <ul className={styles.list}>
                {visibleMissions.map((mission) => (
                <li key={mission.id}>
                  <button
                    type="button"
                    className={styles.card}
                    onClick={() => void openMissionChat(mission)}
                  >
                    <span
                      className={styles.cardArt}
                      style={{ background: serviceTint(mission.service_name) }}
                    >
                      <img src={serviceImage(mission.service_name)} alt="" />
                    </span>
                    <div className={styles.cardInfo}>
                      <p className={styles.serviceName}>{mission.service_name}</p>
                      <p className={styles.meta}>
                        <MapPin size={14} />
                        <span className={styles.metaText}>{mission.address}</span>
                      </p>
                      <p className={styles.meta}>
                        <Calendar size={14} />
                        <span className={styles.metaDate}>
                          {formatSlot(mission.preferred_date, mission.preferred_time)}
                        </span>
                      </p>
                    </div>
                    <StatusBadge status={mission.status} />
                  </button>
                </li>
                ))}
              </ul>

              {visibleMissions.length === 0 ? (
                <p className={styles.noMatch}>
                  Aucune mission avec le statut « {statusMeta(statusFilter).label} ».
                </p>
              ) : null}
            </>
          )
        ) : demands.length === 0 ? (
          <EmptyState
            illustration="/assets/illustrations/search.png"
            title="Aucune demande disponible"
            text={
              myServiceNames.length === 0
                ? "Vous n'avez aucun domaine d'expertise enregistré. Ajoutez vos services depuis votre profil pour recevoir des demandes."
                : pendingTotal > 0
                  ? `${pendingTotal} demande${pendingTotal > 1 ? 's' : ''} en attente sur la plateforme, mais aucune ne correspond à vos services (${myServiceNames.join(', ')})${providerCity ? ` dans votre ville (${providerCity})` : ''}.`
                  : 'Revenez plus tard pour voir de nouvelles missions dans votre ville.'
            }
            action={
              <Link className="btn btn--outline" to="/pro/profile">
                Modifier mes services et ma ville
              </Link>
            }
          />
        ) : (
          <ul className={styles.list}>
            {demands.map((demand) => {
              const applied = appliedIds.has(demand.id);
              return (
                <li key={demand.id}>
                  <button
                    type="button"
                    className={styles.card}
                    onClick={() => navigate(`/pro/requests/${demand.id}/apply`)}
                    disabled={applied}
                  >
                    <span
                      className={styles.cardArt}
                      style={{ background: serviceTint(demand.service_name) }}
                    >
                      <img src={serviceImage(demand.service_name)} alt="" />
                    </span>
                    <div className={styles.cardInfo}>
                      <p className={styles.serviceName}>{demand.service_name}</p>
                      <p className={styles.meta}>
                        <MapPin size={14} />
                        <span className={styles.metaText}>{demand.address}</span>
                      </p>
                      <p className={styles.meta}>
                        <Calendar size={14} />
                        <span className={styles.metaDate}>
                          {formatSlot(demand.preferred_date, demand.preferred_time)}
                        </span>
                      </p>
                    </div>

                    {applied ? (
                      <span className={styles.appliedBadge}>
                        <Clock size={13} /> Postulé
                      </span>
                    ) : (
                      <span className={styles.applyBadge}>
                        Postuler <ChevronRight size={13} />
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
